import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { supabase } from '../supabaseClient';

function Settings() {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [isTrialUser, setIsTrialUser] = useState(true);
  const [subscriptionType, setSubscriptionType] = useState('Free Trial');

  const [morningTime, setMorningTime] = useState('07:00');
  const [middayTime, setMiddayTime] = useState('12:00');
  const [eveningTime, setEveningTime] = useState('21:00');
  const [remindersEnabled, setRemindersEnabled] = useState(true);
  const [selectedVoice, setSelectedVoice] = useState('voice1');
  const [selectedMusic, setSelectedMusic] = useState('epic');
  const [saveMessage, setSaveMessage] = useState('');
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [notificationsSupported, setNotificationsSupported] = useState(false);

  useEffect(() => {
    loadUserAndSettings();
    setNotificationsSupported('Notification' in window);
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  const loadUserAndSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }
      setUser(user);
      setEmail(user.email);

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('first_name')
        .eq('id', user.id)
        .single();

      if (profile?.first_name) {
        setFirstName(profile.first_name);
      } else {
        const metadata = user.user_metadata || {};
        setFirstName(metadata.first_name || metadata.name || user.email?.split('@')[0] || 'User');
      }

      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('subscription_status, subscription_plan, trial_ends_at')
        .eq('user_id', user.id)
        .single();

      if (subscription) {
        if (subscription.subscription_status === 'active') {
          setIsTrialUser(false);
          const planLabel = subscription.subscription_plan === 'annual' ? 'Annual Plan' : 'Monthly Plan';
          setSubscriptionType(planLabel);
        } else if (subscription.trial_ends_at) {
          const daysLeft = Math.ceil(
            (new Date(subscription.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24)
          );
          setIsTrialUser(true);
          setSubscriptionType(`Free Trial (${Math.max(0, daysLeft)} days left)`);
        }
      }

      const { data: settings, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading settings:', error);
        return;
      }

      if (settings) {
        setMorningTime(settings.morning_time || '07:00');
        setMiddayTime(settings.midday_time || '12:00');
        setEveningTime(settings.evening_time || '21:00');
        setRemindersEnabled(settings.reminders_enabled ?? true);
        setSelectedVoice(settings.selected_voice || 'voice1');
        setSelectedMusic(settings.selected_music || 'epic');
      }
    } catch (error) {
      console.error('Error in loadUserAndSettings:', error);
    }
  };

  const getOneSignalPlayerId = async () => {
    try {
      if (!window.OneSignal) return null;
      for (let i = 0; i < 8; i++) {
        const id = window.OneSignal?.User?.PushSubscription?.id;
        if (id) {
          console.log('Got player ID via PushSubscription:', id);
          return id;
        }
        try {
          const legacyId = await window.OneSignal.getUserId?.();
          if (legacyId) {
            console.log('Got player ID via getUserId:', legacyId);
            return legacyId;
          }
        } catch (e) {}
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      console.warn('Could not get OneSignal player ID after retries');
      return null;
    } catch (error) {
      console.error('Error getting OneSignal player ID:', error);
      return null;
    }
  };

  const requestNotificationPermission = async () => {
    try {
      if (!notificationsSupported || !window.OneSignal) return false;
      await window.OneSignal.showSlidedownPrompt();
      await new Promise(resolve => setTimeout(resolve, 2000));
      const permission = Notification.permission;
      setNotificationPermission(permission);
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  };

  const handleSave = async () => {
    if (!user) {
      setSaveMessage('Please log in to save settings');
      setTimeout(() => setSaveMessage(''), 3000);
      return;
    }

    try {
      let playerId = null;

      if (remindersEnabled && notificationsSupported) {
        if (notificationPermission === 'default') {
          await requestNotificationPermission();
        }
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          playerId = await getOneSignalPlayerId();
        }
      }

      const settingsData = {
        user_id: user.id,
        morning_time: morningTime,
        midday_time: middayTime,
        evening_time: eveningTime,
        reminders_enabled: remindersEnabled,
        selected_voice: selectedVoice,
        selected_music: selectedMusic,
        updated_at: new Date().toISOString(),
        created_at: new Date().toISOString(),
        ...(playerId && { onesignal_player_id: playerId }),
      };

      const { error: upsertError } = await supabase
        .from('user_settings')
        .upsert(settingsData, { onConflict: 'user_id' });

      if (upsertError) throw upsertError;

      setSaveMessage('Settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage('Error saving settings. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleManageSubscription = async () => {
    if (isTrialUser) {
      navigate('/payment');
      return;
    }

    try {
      // Get customer ID from subscription
      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('stripe_customer_id')
        .eq('user_id', user.id)
        .single();

      if (!subscription?.stripe_customer_id) {
        alert('No subscription found');
        return;
      }

      // Call API to create portal session
      const response = await fetch('/api/create-portal-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: subscription.stripe_customer_id }),
      });

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error('Error creating portal session:', error);
      alert('Error opening subscription management. Please try again.');
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backButton} onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        <h1 style={styles.title}>Settings</h1>
      </div>

      <div style={styles.content}>
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Account</h2>
          <div style={styles.card}>
            <div style={styles.infoRow}>
              <span style={styles.label}>Name:</span>
              <span style={styles.value}>{firstName}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.label}>Email:</span>
              <span style={styles.value}>{email}</span>
            </div>
            <div style={styles.infoRow}>
              <span style={styles.label}>Subscription:</span>
              <span style={styles.value}>{subscriptionType}</span>
            </div>
            {isTrialUser ? (
              <button style={styles.manageButton} onClick={handleManageSubscription}>
                Upgrade to Premium
              </button>
            ) : (
              <button style={styles.manageButton} onClick={handleManageSubscription}>
                Manage Subscription
              </button>
            )}
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Daily Reminders</h2>
          <div style={styles.card}>
            <div style={styles.toggleRow}>
              <span style={styles.label}>Enable Reminders</span>
              <input
                type="checkbox"
                checked={remindersEnabled}
                onChange={(e) => setRemindersEnabled(e.target.checked)}
                style={styles.checkbox}
              />
            </div>

            {remindersEnabled && (
              <div style={styles.notificationInfo}>
                <p style={styles.noticeText}>
                  ℹ️ <strong>Important:</strong> At this point, iOS does not permit notifications to be sent. These notifications are currently meant to work on desktop computers only. We recommend using your phone's reminder or alarm function to set reminders to ensure success of this transformative program.
                </p>
              </div>
            )}

            {notificationsSupported && notificationPermission === 'default' && remindersEnabled && (
              <div style={styles.permissionNotice}>
                <p style={styles.noticeText}>
                  📬 Click "Save Settings" below to enable push notifications on desktop
                </p>
              </div>
            )}

            {notificationsSupported && notificationPermission === 'denied' && remindersEnabled && (
              <div style={styles.permissionWarning}>
                <p style={styles.noticeText}>
                  ⚠️ Notifications blocked. Please enable in your browser settings.
                </p>
              </div>
            )}

            {notificationsSupported && notificationPermission === 'granted' && remindersEnabled && (
              <div style={styles.permissionGranted}>
                <p style={styles.noticeText}>
                  ✅ Desktop push notifications enabled
                </p>
              </div>
            )}

            {remindersEnabled && (
              <div style={styles.timesContainer}>
                <div style={styles.timeRow}>
                  <label style={styles.timeLabel}>🌅 Morning Reading</label>
                  <input
                    type="time"
                    value={morningTime}
                    onChange={(e) => setMorningTime(e.target.value)}
                    style={styles.timeInput}
                  />
                </div>
                <div style={styles.timeRow}>
                  <label style={styles.timeLabel}>☀️ Midday Reading</label>
                  <input
                    type="time"
                    value={middayTime}
                    onChange={(e) => setMiddayTime(e.target.value)}
                    style={styles.timeInput}
                  />
                </div>
                <div style={styles.timeRow}>
                  <label style={styles.timeLabel}>🌙 Evening Reading</label>
                  <input
                    type="time"
                    value={eveningTime}
                    onChange={(e) => setEveningTime(e.target.value)}
                    style={styles.timeInput}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Audio Preferences</h2>
          <div style={styles.card}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Voice</label>
              <select
                style={styles.select}
                value={selectedVoice}
                onChange={(e) => setSelectedVoice(e.target.value)}
              >
                <option value="my_voice">My Own Voice (Recorded)</option>
                <option value="ai_voice" disabled>AI Voices - Coming Soon</option>
              </select>
              <p style={styles.helperText}>
                Record your own voice reading the scrolls in the Dashboard
              </p>
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Default Background Music</label>
              <select
                style={styles.select}
                value={selectedMusic}
                onChange={(e) => setSelectedMusic(e.target.value)}
              >
                <option value="epic">Epic / Cinematic</option>
                <option value="meditation">Meditation / Calm</option>
                <option value="ambient">Ambient</option>
                <option value="inspire">Inspire</option>
                <option value="dawn">Dawn</option>
                <option value="none">None</option>
              </select>
            </div>
          </div>
        </div>

        <button style={styles.saveButton} onClick={handleSave}>
          💾 Save Settings
        </button>

        {saveMessage && (
          <div style={saveMessage.includes('Error')
            ? styles.errorMessage
            : styles.successMessage}>
            {saveMessage}
          </div>
        )}

        <button style={styles.logoutButton} onClick={handleLogout}>
          Logout
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: theme.colors.bgDark,
    padding: '20px',
  },
  header: {
    maxWidth: '800px',
    margin: '0 auto 32px',
  },
  backButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.colors.teal,
    fontSize: '0.875rem',
    cursor: 'pointer',
    marginBottom: '16px',
    padding: '0.5714em 0',
  },
  title: {
    fontSize: '2.25rem',
    fontWeight: '700',
    background: theme.colors.gradient,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  content: {
    maxWidth: '800px',
    margin: '0 auto',
  },
  section: {
    marginBottom: '32px',
  },
  sectionTitle: {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginBottom: '16px',
  },
  card: {
    backgroundColor: theme.colors.bgMedium,
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid ' + theme.colors.bgLight,
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    paddingBottom: '16px',
    borderBottom: '1px solid ' + theme.colors.bgLight,
  },
  label: {
    color: theme.colors.textSecondary,
    fontSize: '0.875rem',
    fontWeight: '500',
  },
  value: {
    color: theme.colors.textPrimary,
    fontSize: '0.875rem',
  },
  manageButton: {
    background: theme.colors.gradient,
    border: 'none',
    borderRadius: '8px',
    padding: '0.8571em 1.7143em',
    color: 'white',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
  },
  toggleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  checkbox: {
    width: '20px',
    height: '20px',
    cursor: 'pointer',
  },
  notificationInfo: {
    backgroundColor: '#3b82f620',
    border: '1px solid #3b82f6',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '16px',
  },
  permissionNotice: {
    backgroundColor: theme.colors.teal + '20',
    border: '1px solid ' + theme.colors.teal,
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '16px',
  },
  permissionWarning: {
    backgroundColor: '#ff980020',
    border: '1px solid #ff9800',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '16px',
  },
  permissionGranted: {
    backgroundColor: theme.colors.teal + '20',
    border: '1px solid ' + theme.colors.teal,
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '16px',
  },
  noticeText: {
    color: theme.colors.textPrimary,
    fontSize: '0.875rem',
    margin: 0,
  },
  timesContainer: {
    marginTop: '16px',
  },
  timeRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  timeLabel: {
    color: theme.colors.textSecondary,
    fontSize: '0.875rem',
  },
  timeInput: {
    backgroundColor: theme.colors.bgLight,
    border: '1px solid ' + theme.colors.bgLight,
    borderRadius: '8px',
    padding: '0.5714em 0.8571em',
    color: theme.colors.textPrimary,
    fontSize: '0.875rem',
  },
  inputGroup: {
    marginBottom: '20px',
  },
  select: {
    backgroundColor: theme.colors.bgLight,
    border: '1px solid ' + theme.colors.bgLight,
    borderRadius: '8px',
    padding: '0.8571em',
    color: theme.colors.textPrimary,
    fontSize: '0.875rem',
    width: '100%',
    marginTop: '8px',
    cursor: 'pointer',
  },
  saveButton: {
    background: theme.colors.gradient,
    border: 'none',
    borderRadius: '8px',
    padding: '0.875em',
    color: 'white',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    marginBottom: '16px',
  },
  successMessage: {
    backgroundColor: theme.colors.green,
    color: 'white',
    padding: '12px',
    borderRadius: '8px',
    textAlign: 'center',
    marginBottom: '16px',
  },
  errorMessage: {
    backgroundColor: '#f44336',
    color: 'white',
    padding: '12px',
    borderRadius: '8px',
    textAlign: 'center',
    marginBottom: '16px',
  },
  logoutButton: {
    backgroundColor: 'transparent',
    border: '1px solid ' + theme.colors.teal,
    color: theme.colors.teal,
    borderRadius: '8px',
    padding: '0.8571em',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    width: '100%',
  },
  helperText: {
    fontSize: '0.75rem',
    color: theme.colors.textMuted,
    marginTop: '8px',
    fontStyle: 'italic',
  },
};

export default Settings;